import { getOssToken } from '@/api/index'; // 获取OSS上传token的接口
import * as OSS from 'ali-oss';
import axios from 'axios';
import dayjs from 'dayjs';
import md5 from "md5-js/md5";
import Vue from 'vue';


function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height
      });
    };
    
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    
    img.src = url;
  });
}

/**
 * 普通上传至OSS（适用于小文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录（例如 'uploads/')
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadSmallFileToOSS(file, dir = '',is_size_limit = false) {
  console.log('file-type',file.type);
  if (file.type.startsWith('image/') && is_size_limit) {
    try {
      const { width, height } = await getImageDimensions(file);
      const totalPixels = width * height;
      console.log('width',width,'height',height,'totalPixels',totalPixels);
      // 校验宽度不能超过 4096px
      if (width > 4096) {
        // Vue.prototype.$layer.msg(`图片宽度过大(${width}px)，最大允许4096px`);
        throw new Error('Image width exceeds limit');
      }
      
      // 校验总像素不能超过 9437184 (约等于 4096*2304 或 3072*3072)
      if (totalPixels > 9437184) {
        // Vue.prototype.$layer.msg(`图片像素过大(${totalPixels})，最大允许9437184像素`);
        throw new Error('Image total pixels exceed limit');
      }
    } catch (e) {      
      const errorMsg = (e && e.message) ? e.message : String(e);
      // 如果是因为校验失败抛出的错误，直接中断上传
      if (errorMsg.includes('limit') || errorMsg.endsWith('limit')) {
        // console.log('图片像素过大，请检查图片尺寸');
        const customError = new Error('IMAGE_SIZE_LIMIT_EXCEEDED');
        customError.code = 'IMAGE_SIZE_LIMIT'; 
        return Promise.reject(customError);
      }
      // 如果是读取图片失败，可以选择警告但继续上传，或者中断
      console.warn('无法读取图片尺寸，跳过尺寸校验', e);
    }
  }
  try {
    Vue.prototype.$globalLoading.show('文件上传中...', true);

    // 1. 获取OSS上传配置
    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({dir:dir+'/'+date});
    console.log('ossConfig',ossConfig);

    // 2. 确保dir以"tssd/"开头
    if (!dir.startsWith('yichuanghub/')) {
      dir = `yichuanghub/${dir}/${date}`;
    }

    // 3. 构建文件存储路径（如果文件名本身不包含目录，则添加时间戳防止重名）
    // const timestamp = Math.floor(Date.now() / 1000);
    const nameHash = await getFileHash(file);
    const ext = file.name.split('.').pop();
    // const fileName = file.name.includes('/') ? file.name : `${timestamp}-${file.name}`;
    const fileName = `${nameHash}.${ext}`;

    const key = dir.endsWith('/') ? dir : dir+'/'+fileName;


    // 4. 创建FormData对象
    const formData = new FormData();
    formData.append('key', key);
    formData.append('policy', ossConfig.policy);
    formData.append('OSSAccessKeyId', ossConfig.accessKeyId);
    formData.append('signature', ossConfig.signature);
    formData.append('success_action_status', '200'); // 确保返回200状态码
    formData.append('Content-Disposition', 'filename=' + encodeURIComponent(file.name)); // 确保返回200状态码
    formData.append('file', file);

    // 5. 执行上传
    const response = await axios.post(ossConfig.host, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'api-usage': 'oss'
      },
      timeout: 300000,
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        Vue.prototype.$globalLoading.updateProgress(percent);
      }
    }).finally(()=>{
      Vue.prototype.$globalLoading.hide();
    });

    console.log('response',response);

    // 6. 返回文件URL
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    console.error('上传失败-1:', error);

    // 错误处理
    if (error.response) {
      // 服务器响应了错误的状态码
      console.error('服务器响应:', error.response.status, error.response.data);

      // 如果是签名过期，可以尝试重新获取签名并重试
      if (error.response.status === 403) {
        console.log('检测到403错误，尝试刷新签名后重试');

        // 重新获取OSS token
        const { data: newOssConfig } = await getOssToken();

        // 再次尝试上传
        const retryResponse = await axios.post(newOssConfig.host, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'api-usage': 'oss'
          },
          timeout: 30000
        });

        return `${newOssConfig.host}/${key}`;
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('无响应:', error.request);
    } else {
      // 其他错误
      console.error('请求设置出错:', error.message);
    }

    throw error;
  }
}

/**
 * 分片上传至OSS（适用于大文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadLargeFileToOSS(file, dir = '') {
  const client = new OSS({
    region: 'oss-cn-hangzhou', // 替换为你的OSS区域
    accessKeyId: '', // 将在获取token时填充
    accessKeySecret: '', // 将在获取token时填充
    stsToken: '', // 将在获取token时填充
    bucket: 'your-bucket-name', // 替换为你的OSS Bucket名称
    secure: true,
  });

  try {
    // 获取临时上传token
    const { data } = await getOssToken();
    const { AccessKeyId, AccessKeySecret, SecurityToken, host } = data;

    // 更新client配置
    client.options.accessKeyId = AccessKeyId;
    client.options.accessKeySecret = AccessKeySecret;
    client.options.stsToken = SecurityToken;

    const key = `${dir}${file.name}`;
    const partSize = 5 * 1024 * 1024; // 每个分片大小（5MB）
    const chunks = Math.ceil(file.size / partSize);
    let uploadId = '';

    // Step 1: 初始化分片上传任务
    const initResult = await client.initMultipartUpload(key);
    uploadId = initResult.uploadId;

    // Step 2: 并行上传各个分片
    const partETags = [];
    const partPromises = [];

    for (let i = 0; i < chunks; i++) {
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      partPromises.push(
        new Promise(async (resolve, reject) => {
          try {
            const result = await client.uploadPart(key, uploadId, i + 1, chunk);
            partETags.push({ PartNumber: i + 1, ETag: result.etag });
            resolve();
          } catch (err) {
            reject(err);
          }
        })
      );
    }

    await Promise.all(partPromises);

    // Step 3: 完成分片上传
    await client.completeMultipartUpload(key, uploadId, partETags);

    // 返回上传后的URL
    return `${host}/${key}`;
  } catch (error) {
    console.error('上传失败:', error);
    throw error;
  }
}

/**
 * 自动判断上传方式（普通 or 分片）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @param {number} [threshold=5] 判断阈值（单位：MB）
 * @param {boolean} is_size_limit 是否限制文件大小
 * @returns {Promise<string>}
 */
export async function uploadFileToOSS(file, dir = '',is_size_limit = false,threshold = 5) {
    //>暂时只保留小文件直传
    return await uploadSmallFileToOSS(file, dir,is_size_limit);
  // const fileSizeInMB = file.size / (1024 * 1024); // 文件大小转换为 MB
  // if (fileSizeInMB < threshold) {
  //   return await uploadSmallFileToOSS(file, dir);
  // } else {
  //   return await uploadLargeFileToOSS(file, dir);
  // }
}


async function getFileHash(file) {
  // 首先尝试使用Web Crypto API (现代浏览器)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
      
      // 将 ArrayBuffer 转换为十六进制字符串
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      return hashHex;
    } catch (error) {
      console.warn('Web Crypto API failed, falling back to MD5', error);
    }
  }
  
  // 降级方案：使用MD5 (兼容旧浏览器)
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        // 使用项目中已有的md5-js包
        const hash = md5(e.target.result);
        resolve(hash);
      } catch (error) {
        // 如果md5-js也失败了，使用简单的备用方案
        // 这里使用一个简单的方法生成基于文件名和大小的"hash"
        const fallbackHash = btoa(file.name + file.size).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        resolve(fallbackHash.substring(0, 40)); // SHA-1长度为40字符
      }
    };
    reader.onerror = function() {
      // 文件读取出错时的备用方案
      const fallbackHash = btoa(file.name + file.size).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      resolve(fallbackHash.substring(0, 40));
    };
    reader.readAsBinaryString(file);
  });
}
